import { api } from './client.js';

/**
 * Summary of an active or historical impersonation session.
 * Returned by all impersonation endpoints.
 */
export interface ImpersonationSessionSummary {
  id: string;
  adminUserId: string;
  storeId: number;
  storeName: string;
  startedAt: string;
  expiresAt: string;
  hardCeilingAt: string;
  writeMode: boolean;
  mutationCount: number;
  denyListHits: number;
}

/**
 * Start impersonating a store as an admin user.
 * Only OWNER-role users can initiate impersonation.
 * Returns the new session summary.
 *
 * @param storeId - The store ID to impersonate
 * @throws ApiError on failure
 */
export async function startImpersonation(storeId: number): Promise<ImpersonationSessionSummary> {
  const res = await api.post<ImpersonationSessionSummary>(
    '/api/v1/admin/impersonation/start',
    { storeId },
  );
  if (!res.success || !res.data) {
    throw new Error(res.error ?? 'Failed to start impersonation');
  }
  return res.data;
}

/**
 * Stop the current impersonation session.
 * Only callable by the admin user whose session is active.
 *
 * @throws ApiError on failure
 */
export async function stopImpersonation(): Promise<void> {
  const res = await api.post<null>('/api/v1/admin/impersonation/stop', {});
  if (!res.success) {
    throw new Error(res.error ?? 'Failed to stop impersonation');
  }
}

/**
 * Enable write mode for the current impersonation session.
 * Write mode is a gated feature to allow mutation operations.
 * Only callable once per session.
 *
 * @throws ApiError on failure
 */
export async function enableImpersonationWriteMode(): Promise<{
  id: string;
  writeMode: boolean;
  writeModeEnabledAt?: string;
}> {
  const res = await api.post<{ id: string; writeMode: boolean; writeModeEnabledAt?: string }>(
    '/api/v1/admin/impersonation/write-mode',
    {},
  );
  if (!res.success || !res.data) {
    throw new Error(res.error ?? 'Failed to enable write mode');
  }
  return res.data;
}

/**
 * Fetch the current active impersonation session for the authenticated user.
 * Returns null if no session is active.
 *
 * @throws ApiError on failure
 */
export async function getCurrentImpersonation(): Promise<ImpersonationSessionSummary | null> {
  const res = await api.get<ImpersonationSessionSummary | null>(
    '/api/v1/admin/impersonation/current',
  );
  if (!res.success) {
    throw new Error(res.error ?? 'Failed to fetch current session');
  }
  return res.data ?? null;
}
